import classNames from 'classnames';
import { Call as CallModel } from '../models/call';
import styles from './Call.module.scss';
import { timeToClockFormat } from '@shared/utils/time';
import { useMemo, useRef } from 'react';
import { VehicleType } from '../models/vehicleType';
import useCallDisplayTime from '../hooks/useCallDisplayTime';
import ImageIcon from '@shared/components/ImageIcon';
import { longestWordWidth } from '@shared/utils/longestWordWidth';
import useElementSize from '@shared/hooks/useElementSize';

export interface CallParams {
  id: string;
  vehicleType: VehicleType;
  call: CallModel;
  isCurrent: boolean;
  isStopPressed: boolean | undefined;
  isAtCollapse: boolean;
  isPrevious: boolean;
  isPassed: boolean;
  isSecondLast: boolean;
  isLast: boolean;
  isMovingTowardsNextStop: boolean;
  isSticky: boolean;
}

const STOP_AREA_FONT = 'H2';
const STOP_AREA_CURRENT_FONT = 'H1';

export default function Call({
  id,
  vehicleType,
  call,
  isCurrent,
  isStopPressed,
  isAtCollapse,
  isPrevious,
  isPassed,
  isSecondLast,
  isLast,
  isMovingTowardsNextStop,
  isSticky,
}: CallParams) {
  const stopAreaNameRef = useRef<HTMLDivElement>(null);
  const displayTime = useCallDisplayTime(call, vehicleType);

  const availableWidth = useElementSize(stopAreaNameRef).width;

  const stopAreaFont = isCurrent ? STOP_AREA_CURRENT_FONT : STOP_AREA_FONT;

  const stopAreaText = useMemo(() => {
    if (call.stopAreaName === undefined) {
      return undefined;
    }

    const fullNameFits =
      availableWidth === undefined ||
      longestWordWidth(call.stopAreaName, stopAreaFont) <= availableWidth;
    if (fullNameFits) {
      return call.stopAreaName;
    }

    return call.stopAreaShortName ?? call.stopAreaName;
  }, [call.stopAreaName, call.stopAreaShortName, availableWidth, stopAreaFont]);

  const stopAreaNeedEllipsis = useMemo(() => {
    if (availableWidth === undefined || stopAreaText === undefined) {
      return false;
    }
    return longestWordWidth(stopAreaText, stopAreaFont) > availableWidth;
  }, [stopAreaText, availableWidth, stopAreaFont]);

  return (
    <div
      data-id={id}
      className={classNames(
        styles.stop,
        isCurrent && styles['stop--current'],
        isSticky && styles['stop--sticky'],
      )}
    >
      <div className={styles.stop__visual}>
        {!isLast && (
          <div
            className={classNames(
              styles['stop__visual__path-next'],
              isPrevious &&
                isMovingTowardsNextStop &&
                styles['stop__visual__path-next--moving-from'],
              isPassed &&
                !(isPrevious && isMovingTowardsNextStop) &&
                styles['stop__visual__path-next--passed'],
              isAtCollapse &&
                !isSecondLast &&
                styles['stop__visual__path-next--at-collapse'],
            )}
          ></div>
        )}
        <div
          className={classNames(
            styles.stop__visual__location,
            isCurrent && styles['stop__visual__location--current'],
            isCurrent &&
              !isMovingTowardsNextStop &&
              styles['stop__visual__location--at-current'],
            isPassed && styles['stop__visual__location--passed'],
            call.type === 'cancelled' &&
              styles['stop__visual__location--cancelled'],
            isLast && styles['stop__visual__location--last'],
          )}
        ></div>
      </div>
      <div
        ref={stopAreaNameRef}
        className={classNames(
          styles.stop__name,
          call.type === 'cancelled' && styles['stop__name--cancelled'],
          call.type === 'passed' && styles['stop__name--passed'],
          isCurrent && styles['stop__name--current'],
          stopAreaNeedEllipsis && styles['stop__name--overflown'],
        )}
      >
        {stopAreaText}
        {(call.type === 'basic' ||
          call.type === 'delayed' ||
          call.type === 'upcoming') &&
          call.isAlightingAllowed === false && (
            <div
              className={classNames(
                styles.tag,
                isCurrent && styles['tag--current'],
              )}
            >
              <ImageIcon type="no" alt=""></ImageIcon> Ingen avstigning
            </div>
          )}
      </div>
      {isStopPressed && isCurrent && (
        <span className={styles.stopping}>Stannar</span>
      )}
      {call.type === 'awaiting-departure' && displayTime !== undefined && (
        <span className={classNames(styles['departure-time'])}>
          <span className={styles['departure-time__departure']}>
            {vehicleType === 'train' || displayTime === 'Nu'
              ? 'Avgår'
              : 'Avgår om'}
          </span>
          {vehicleType === 'other' && (
            <span role="timer" className={styles['departure-time__time']}>
              {displayTime}
            </span>
          )}
          {vehicleType === 'train' && (
            <time className={styles['departure-time__time']}>
              {displayTime}
            </time>
          )}
        </span>
      )}
      {call.type === 'cancelled' && (
        <span className={styles['arrival-cancelled']}>Inställd</span>
      )}
      {vehicleType === 'train' && call.type === 'delayed' && (
        <time className={classNames(styles['new-arrival-time'])}>
          {timeToClockFormat(call.arrivalTime)}
        </time>
      )}
      {vehicleType === 'train' && call.type === 'upcoming' && (
        <time className={classNames(styles['target-arrival-time'])}>
          {timeToClockFormat(call.arrivalTime)}
        </time>
      )}
      {vehicleType === 'train' && call.type === 'delayed' && (
        <time
          className={classNames(
            styles['target-arrival-time'],
            styles['target-arrival-time--changed'],
          )}
        >
          {timeToClockFormat(call.targetArrivalTime)}
        </time>
      )}
      {vehicleType === 'other' &&
        (call.type === 'upcoming' || call.type === 'delayed') &&
        displayTime !== undefined && (
          <span role="timer" className={classNames(styles['arrival-time'])}>
            {displayTime}
          </span>
        )}
      {vehicleType === 'train' &&
        call.type === 'cancelled' &&
        call.arrivalTime && (
          <time
            className={classNames(
              styles['target-arrival-time'],
              styles['target-arrival-time--cancelled'],
            )}
          >
            {timeToClockFormat(call.arrivalTime)}
          </time>
        )}
      {vehicleType === 'train' &&
        call.type === 'passed' &&
        call.departedTime && (
          <time className={classNames(styles['departed-time'])}>
            {timeToClockFormat(call.departedTime)}
          </time>
        )}
      {vehicleType === 'other' && call.type === 'passed' && (
        <span className={classNames(styles['passed'])}>Passerad</span>
      )}
    </div>
  );
}
